import { useEffect, useState } from "react"

const DEFAULT_STEPS = [
  `Copying your itinerary...`,
  `Setting up your days...`,
  `Moving your places...`,
  `Almost ready...`,
]

interface CopyingTripOverlayProps {
  trip: { name?: string | null; destination: string; image_url?: string | null } | null
  /** Small uppercase label above the title. */
  label?: string
  /** Cycling status messages shown at the bottom of the card. */
  steps?: string[]
}

export function CopyingTripOverlay({
  trip,
  label = `Saving to your trips`,
  steps = DEFAULT_STEPS,
}: CopyingTripOverlayProps) {
  const [stepIndex, setStepIndex] = useState(0)

  useEffect(() => {
    if (!trip) return
    setStepIndex(0)
    const id = setInterval(() => {
      setStepIndex((i) => Math.min(i + 1, steps.length - 1))
    }, 1400)
    return () => clearInterval(id)
  }, [trip, steps.length])

  if (!trip) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Blurred backdrop */}
      {trip.image_url && (
        <div
          className="absolute inset-0 bg-cover bg-center scale-105"
          style={{ backgroundImage: `url(${trip.image_url})`, filter: `blur(24px) brightness(0.4)` }}
        />
      )}
      <div className="absolute inset-0 bg-black/60" />

      {/* Card */}
      <div className="relative z-10 flex flex-col items-center gap-6 px-8 py-10 rounded-2xl bg-white/10 backdrop-blur-md border border-white/20 max-w-sm w-full mx-4 text-white text-center shadow-2xl">
        {trip.image_url && (
          <img
            src={trip.image_url}
            alt={trip.destination}
            className="w-20 h-20 rounded-xl object-cover shadow-lg"
          />
        )}

        <div className="space-y-1">
          <p className="text-xs uppercase tracking-widest text-white/60 font-semibold">{label}</p>
          <h2 className="text-xl font-bold">{trip.name || trip.destination}</h2>
        </div>

        {/* Animated dots */}
        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className="w-2 h-2 rounded-full bg-white animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>

        {/* Cycling status */}
        <p className="text-sm text-white/70 h-5 transition-all duration-300">
          {steps[stepIndex]}
        </p>
      </div>
    </div>
  )
}
